import React, { useCallback, useEffect } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Button from 'fe-design-base/molecules/Button';
import Divider from 'fe-design-base/molecules/Divider';
import TextField from 'fe-design-base/molecules/TextField';
import { Form } from 'formik';

import {
  EMAIL_FIELD_PLACEHOLDER,
  FORGOT_PASSWORD_PATH,
  TEMP_EMAIL_KEY,
} from 'features/authentication/SignIn/constants';

import { toI18n } from 'util/i18n';

type Props = {
  formikHandleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  dirty: boolean;
  isValid: boolean;
  values: {
    email: string;
    password: string;
  };
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
};

const EmailPasswordForm = ({
  formikHandleSubmit,
  isSubmitting,
  dirty,
  isValid,
  values,
  setFieldValue,
}: Props) => {
  const handleKeyDown = useCallback(
    // TODO: https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    e => {
      if (
        e.key === 'Enter' &&
        // User should be able to tab to the password icon to toggle hide/show
        // without submitting the form
        !e.target.querySelector('.FDBTextInput__password-icon')
      ) {
        formikHandleSubmit();
      }
    },
    [formikHandleSubmit]
  );

  useEffect(() => {
    const initialEmail = window.sessionStorage.getItem('TEMP_EMAIL');
    if (initialEmail && !values.email) {
      setFieldValue('email', initialEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    window.sessionStorage.setItem(TEMP_EMAIL_KEY, values.email);
  });

  return (
    <Form role="form" onKeyDown={handleKeyDown}>
      <Box column w="100%">
        <Box column gap={12}>
          <TextField
            name="email"
            label={toI18n('fe_design_base.label.email')}
            fullWidth
            trim
            dataTestId="EmailSignInForm__email-input"
            startIcon="Email"
            placeholder={EMAIL_FIELD_PLACEHOLDER}
          />
          <TextField
            name="password"
            label={toI18n('fe_design_base.label.password')}
            type="password"
            fullWidth
            dataTestId="EmailSignInForm__password-input"
            startIcon="Lock"
            placeholder="********"
          />
        </Box>
        <Box column gap={16} mt={16}>
          <Button
            type="submit"
            isLoading={isSubmitting}
            uxElement="submit button"
            disabled={!dirty || !isValid}
          >
            {toI18n('authentication.sign_in')}
          </Button>
          <Button
            variant="tertiary"
            linkTo={FORGOT_PASSWORD_PATH}
            uxElement="forgot password button"
          >
            {toI18n('authentication.forgot_password_link')}
          </Button>
        </Box>
        <Divider spacing={32} color="mono400" />
      </Box>
    </Form>
  );
};

export default EmailPasswordForm;
